import { AskAlpharunPanel } from "@repo/ui/chat/askAlpharunPanel";
import { type FC, useState } from "react";
import { twMerge } from "tailwind-merge";
import { ResearchAgentChatView } from "./researchAgentChatView.js";
import type { ResearchAgentProps } from "./researchAgentContext.js";
import { ResearchAgentHistoryView } from "./researchAgentHistoryView.js";

/** Content column inside the panel (header, messages, composer). */
const PANEL_CONTENT_WIDTH_PX = 310;

/**
 * Gutter between the body card and the panel content. (The app layout's own
 * padding already provides the gutter at the window edge.)
 */
const PANEL_HORIZONTAL_MARGIN_PX = 8;

/** Default (and minimum) width of the docked panel column. */
export const RESEARCH_AGENT_PANEL_WIDTH_PX =
  PANEL_CONTENT_WIDTH_PX + PANEL_HORIZONTAL_MARGIN_PX;

/** How far the panel column can be resized. */
export const RESEARCH_AGENT_PANEL_MAX_WIDTH_PX = 640;

type PanelView = "chat" | "history";

export interface ResearchAgentSidePanelProps {
  researchAgent: ResearchAgentProps;
}

/**
 * Content of the docked research agent panel: the chat view with a slide-over
 * history view, in the rounded panel card.
 */
export const ResearchAgentSidePanel: FC<ResearchAgentSidePanelProps> = ({
  researchAgent,
}) => {
  const [panelView, setPanelView] = useState<PanelView>("chat");
  // Mount the history layer lazily so opening the panel doesn't fetch the
  // thread list; once visited it stays mounted to keep the cross-fade.
  const [didShowHistory, setDidShowHistory] = useState(false);
  const showHistory = () => {
    setDidShowHistory(true);
    setPanelView("history");
  };

  const handleRequestClose = () => researchAgent.onCloseChat();
  const handleRequestExpand = () => researchAgent.onModalOpenChange(true);

  return (
    <AskAlpharunPanel
      // The app layout's `p-2` already covers the top/bottom/right gutters;
      // keep only the left gutter between the body card and the panel.
      className="h-full w-full py-0 pr-0 pl-2"
    >
      {/* Chat and history are sibling layers; toggling the view cross-fades
          and slides between them without unmounting the chat. While the
          expanded modal is open, neither renders — the modal shows the same
          conversation, and a second live copy would double every streamed
          token's render work. */}
      {!researchAgent.isModalOpen ? (
        <div className="relative min-h-0 min-w-0 flex-1 overflow-clip">
          <div
            // The hidden layer is only visually hidden; inert also removes it
            // from the tab order and accessibility tree. Set via ref because
            // React 18 does not support the `inert` prop.
            ref={(element) => {
              if (element) {
                element.inert = panelView !== "chat";
              }
            }}
            className={twMerge(
              "absolute inset-0 flex min-h-0 min-w-0 flex-col overflow-hidden transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none",
              panelView === "history"
                ? "pointer-events-none -translate-x-4 opacity-0"
                : "translate-x-0 opacity-100",
            )}
          >
            <ResearchAgentChatView
              researchAgent={researchAgent}
              surface="panel"
              onRequestClose={handleRequestClose}
              onRequestExpand={handleRequestExpand}
              onShowHistory={showHistory}
            />
          </div>

          {didShowHistory ? (
            <div
              ref={(element) => {
                if (element) {
                  element.inert = panelView !== "history";
                }
              }}
              className={twMerge(
                "absolute inset-0 min-h-0 min-w-0 transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none",
                panelView === "history"
                  ? "translate-x-0 opacity-100"
                  : "pointer-events-none translate-x-full opacity-0",
              )}
            >
              <ResearchAgentHistoryView
                surface="panel"
                resourceId={researchAgent.resourceId}
                selectedThreadId={researchAgent.threadId}
                onThreadSelect={(thread) => {
                  researchAgent.onThreadSelect(thread);
                  setPanelView("chat");
                }}
                onNewThreadSelect={() => {
                  researchAgent.onNewThreadSelect();
                  setPanelView("chat");
                }}
                onRequestExpand={handleRequestExpand}
                onRequestClose={handleRequestClose}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </AskAlpharunPanel>
  );
};
